import { formatPayout } from '@/lib/binary-market';

export interface SportsMoneylineRowProps {
  /** Team / participant name (or "Draw") — the fallback when no abbreviation. */
  label: string;
  /** Plaee team abbreviation (e.g. "ARG"); shown in place of the name when present. */
  shortName?: string | null;
  /** Plaee team crest URL; rendered in place of the colour dot when present. */
  imageUrl?: string | null;
  /** The effective answer colour (Plaee, else Figma default per side). */
  color: string;
  /** Whole-percent Yes chance; drives the chip label and the underline width. */
  percent: number | null;
  /** Payout multiplier (e.g. 2.46) shown between the name and the chip. */
  payout: number | null;
  /** Opens the buy-in popup pre-set to this answer's Yes side. */
  onSelect: () => void;
}

/**
 * One moneyline answer on the lobby V2 sport card (Figma 5751-141388): team
 * crest + name with a thin 4px team-colour probability underline, the payout
 * multiplier, and a filled percent chip tinted with the team colour (12%
 * fill, full-strength text — the "subtle" chip treatment). The chip stops
 * propagation so it buys without also navigating the card to the event page.
 */
export function SportsMoneylineRow({
  label,
  shortName,
  imageUrl,
  color,
  percent,
  payout,
  onSelect,
}: SportsMoneylineRowProps) {
  const barWidth = percent == null ? 0 : Math.min(100, Math.max(0, percent));
  const name = shortName || label;

  return (
    <div className="flex items-center gap-6 py-1">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1">
          {imageUrl ? (
            <img src={imageUrl} alt={label} className="size-4 shrink-0 rounded-full object-cover" />
          ) : (
            <span className="size-4 shrink-0 rounded-full" style={{ backgroundColor: color }} aria-hidden />
          )}
          <span className="truncate text-[16px] leading-normal tracking-[-0.32px] text-ink">{name}</span>
        </div>
        {/* Probability underline — only the coloured fill, no track behind it. */}
        <span className="block h-[4px] rounded-pill" style={{ width: `${barWidth}%`, backgroundColor: color }} aria-hidden />
      </div>

      <div className="flex shrink-0 items-center gap-1.5">
        <span className="text-[14px] tracking-[-0.28px] text-ink-muted">{formatPayout(payout)}</span>
        <button
          type="button"
          aria-label={`${label} — ${percent == null ? 'no price' : `${percent}% Yes`}`}
          onClick={(e) => {
            e.stopPropagation();
            onSelect();
          }}
          className="flex h-8 w-[74px] items-center justify-center rounded-pill transition-opacity hover:opacity-80"
          style={{ backgroundColor: `color-mix(in srgb, ${color} 12%, transparent)`, color }}
        >
          <span className="text-[16px] font-semibold tracking-[-0.32px]">{percent == null ? '—' : `${percent}%`}</span>
        </button>
      </div>
    </div>
  );
}
